import React from 'react';
import { Coins, KeyRound, ExternalLink } from 'lucide-react';

const TOKEN_PRODUCT_URL = process.env.REACT_APP_TOKEN_PRODUCT_URL || '';
const TOKEN_VALUE_USD = 5;

/**
 * Course CTA — $5 Token Standard
 * -------------------------------
 * Architect directive (2026-05-31):
 *   • Paid courses no longer link out to per-course Gumroad listings.
 *   • Enroll → fires `scholar:open-redeem` which expands the
 *     RedeemCodePanel and focuses its code input.
 *   • Secondary "Buy N Tokens" CTA opens the universal token product
 *     on Gumroad with ?quantity=N pre-filled.
 *   • Free courses keep their original "Enroll FREE" branch.
 */
export default function CourseCTA({ course, onBrowseMore }) {
  const price = Number(course?.price) || 0;
  const tokens = price > 0 ? Math.ceil(price / TOKEN_VALUE_USD) : 0;
  const buyUrl = TOKEN_PRODUCT_URL
    ? `${TOKEN_PRODUCT_URL}${TOKEN_PRODUCT_URL.includes('?') ? '&' : '?'}quantity=${tokens}`
    : '';

  const openRedeem = () => {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('scholar:open-redeem'));
    }
  };

  return (
    <div className="bg-gradient-to-r from-purple-900 to-pink-900 py-16" data-testid="course-cta">
      <div className="container mx-auto px-4 text-center">
        <h2 className="text-3xl md:text-4xl font-bold mb-4">Ready to Begin Your Journey?</h2>
        <p className="text-xl text-gray-200 mb-8 max-w-2xl mx-auto">
          Join the NextXus Federation's mission to elevate human knowledge for the next 200 years.
        </p>

        {price > 0 ? (
          <>
            <div className="flex items-center justify-center gap-4 flex-wrap">
              <button
                type="button"
                onClick={openRedeem}
                data-testid="course-enroll-cta"
                data-action="open-redeem"
                className="inline-flex items-center gap-2 bg-amber-400 text-black px-8 py-4 rounded-lg font-bold text-lg hover:bg-amber-300 transition-colors shadow-xl"
              >
                <KeyRound className="w-5 h-5" />
                Enroll Now — Redeem {tokens} Tokens
              </button>

              {buyUrl ? (
                <a
                  href={buyUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="course-buy-tokens-cta"
                  data-gumroad-quantity={tokens}
                  className="inline-flex items-center gap-2 bg-transparent border-2 border-amber-300/70 text-amber-200 px-8 py-4 rounded-lg font-bold text-lg hover:bg-amber-300/10 transition-colors"
                >
                  <Coins className="w-5 h-5" />
                  Buy {tokens} Tokens — ${price.toFixed(0)}
                  <ExternalLink className="w-4 h-4" />
                </a>
              ) : (
                <span
                  data-testid="course-buy-tokens-pending"
                  className="inline-flex items-center gap-2 bg-gray-800 text-gray-400 px-8 py-4 rounded-lg font-bold text-lg cursor-not-allowed"
                >
                  <Coins className="w-5 h-5 opacity-50" />
                  Buy Tokens — link pending
                </span>
              )}

              <button
                onClick={onBrowseMore}
                data-testid="course-browse-more"
                className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-white/10 transition-colors"
              >
                Browse More Courses
              </button>
            </div>
            <p className="text-sm text-amber-200/80 mt-6 font-mono">
              1 Token = ${TOKEN_VALUE_USD} · Tokens spend on any course, book, or Federation offering.
            </p>
          </>
        ) : (
          <div className="flex items-center justify-center gap-4 flex-wrap">
            <button
              data-testid="course-enroll-cta"
              data-action="enroll-free"
              className="bg-white text-purple-900 px-8 py-4 rounded-lg font-bold text-lg shadow-xl hover:bg-gray-100 transition-colors"
            >
              Enroll FREE
            </button>
            <button
              onClick={onBrowseMore}
              data-testid="course-browse-more"
              className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-white/10 transition-colors"
            >
              Browse More Courses
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
